'use client';

/**
 * Shared reader for GET /api/earnings/summary.
 *
 * Several surfaces want the same payload at the same time (the wallet's
 * Creator Rewards card is mounted by both BalanceOverview and BalanceCard, and
 * the analytics Earnings section reads it too). Each of them used to own a
 * `fetch` in a `useEffect`, so a page rendering two of them fired the route
 * twice for one identical, authed, force-dynamic response.
 *
 * This hook de-duplicates at the module level: the first mount starts the
 * request, every concurrent mount awaits the SAME promise, and later mounts
 * within the TTL reuse the resolved value. `refreshEarningsSummary()` drops
 * the cache for flows that change the balance (a withdrawal request, say).
 */

import { useEffect, useState } from 'react';

export interface EarningsDailyBucket {
  date: string;
  usage: string;
  a2a: string;
}

export interface EarningsEvent {
  date: string;
  type: 'grant' | 'payout' | 'adjustment';
  amount: string;
  note: string | null;
}

export interface EarningsPerAppRow {
  appPostId: string;
  title: string | null;
  totalNanocents: string;
  payoutCount: number;
}

export interface EarningsSummary {
  availableNanocents: string;
  lifetimeNanocents: string;
  daily: EarningsDailyBucket[];
  events: EarningsEvent[];
  perApp: EarningsPerAppRow[];
  provisionalDays: number;
  basisCreditsPerUsd: number;
  cashoutDate: string | null;
  minWithdrawalNanocents: string;
  pendingRequest: { status: string; amount: string } | null;
  /** True when the user has any earnings ledger row (accrual, grant, payout). */
  hasEarningsRows: boolean;
  /** True when the user has published at least one app. */
  hasPublishedApps: boolean;
}

/** How long a resolved summary is reused across mounts. */
const TTL_MS = 30_000;

let inFlight: Promise<EarningsSummary | null> | null = null;
let cached: { at: number; value: EarningsSummary | null } | null = null;

async function load(): Promise<EarningsSummary | null> {
  try {
    const res = await fetch('/api/earnings/summary');
    if (!res.ok) return null;
    return (await res.json()) as EarningsSummary;
  } catch {
    return null;
  }
}

export function getEarningsSummary(): Promise<EarningsSummary | null> {
  if (cached && Date.now() - cached.at < TTL_MS) {
    return Promise.resolve(cached.value);
  }
  if (!inFlight) {
    inFlight = load().then((value) => {
      cached = { at: Date.now(), value };
      inFlight = null;
      return value;
    });
  }
  return inFlight;
}

/** Drop the cached summary so the next read hits the route. */
export function refreshEarningsSummary(): void {
  cached = null;
}

export function useEarningsSummary(): {
  summary: EarningsSummary | null;
  loaded: boolean;
} {
  const [summary, setSummary] = useState<EarningsSummary | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getEarningsSummary().then((value) => {
      if (cancelled) return;
      setSummary(value);
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return { summary, loaded };
}
